import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from '@mantine/hooks';

import { LOGO_TWINKLE_EVENT } from '@/components/shared/Logo/ChatcoWordmark';
import { setLogoMotionPaused } from '@/components/shared/Logo/logoMotion';

import { type AskReceipt, type AskScenario, SEED_RECEIPTS } from './askScenarios';

export type AskPhase = 'waiting' | 'swap' | 'typing' | 'reading' | 'found' | 'answering' | 'done';

export type AnswerToken =
  | { kind: 'word'; text: string }
  | { kind: 'cite'; n: number; trail: string };

export interface AskDemoState {
  index: number;
  phase: AskPhase;
  /** Characters of the question typed so far. */
  typed: number;
  /** Index into `reading` currently shown; -1 when not reading. */
  readingStep: number;
  /** Answer tokens revealed so far. */
  shownTokens: number;
  sourcesShown: boolean;
  /** Share of the current scenario elapsed, 0–1, for the tab progress bar. */
  progress: number;
  receipts: AskReceipt[];
}

const CANCELLED = Symbol('cancelled');
const FIRST_DELAY_MS = 1150;
const MAX_RECEIPTS = 5;

export function tokenizeAnswer(answer: string): AnswerToken[] {
  return answer.split(' ').map((raw) => {
    const cite = raw.match(/^\[(\d+)\]([.,;]?)$/);
    return cite
      ? { kind: 'cite', n: Number(cite[1]), trail: cite[2] }
      : { kind: 'word', text: raw };
  });
}

const between = (min: number, max: number) => min + Math.random() * (max - min);
const holdMs = (s: AskScenario) => Math.max(2600, s.answer.length * 17);

/** Rough runtime of one scenario, only used to scale the tab progress bar. */
function estimateMs(s: AskScenario) {
  const tokens = tokenizeAnswer(s.answer);
  return (
    700 +
    s.question.length * 47 +
    260 +
    s.reading.length * 265 +
    420 +
    tokens.length * 58 +
    460 +
    s.sources.length * 140 +
    holdMs(s) +
    250
  );
}

/** A scenario shown from its start: identity and knowledge visible, nothing asked yet. */
function freshFrame(index: number): Omit<AskDemoState, 'receipts'> {
  return {
    index,
    phase: 'waiting',
    typed: 0,
    readingStep: -1,
    shownTokens: 0,
    sourcesShown: false,
    progress: 0,
  };
}

function finishedState(
  index: number,
  scenarios: AskScenario[],
  receipts: AskReceipt[]
): AskDemoState {
  return {
    index,
    phase: 'done',
    typed: scenarios[index].question.length,
    readingStep: -1,
    shownTokens: tokenizeAnswer(scenarios[index].answer).length,
    sourcesShown: true,
    progress: 1,
    receipts,
  };
}

/**
 * Drives the homepage demo: each scenario plays as one job (question typed, knowledge read, cited answer
 * streamed, outcome shown, answer filed into the receipts rail), then the next begins. Time only advances
 * while nothing is holding the demo: the pause button, hover or keyboard focus on the panel, the panel
 * scrolled out of view, or the tab hidden. With reduced motion the chosen scenario renders finished.
 */
export function useAskDemo(scenarios: AskScenario[]) {
  const reducedMotion = useReducedMotion();
  // Bumped by `select` to restart playback; the scenario to play is whatever is on screen.
  const [generation, setGeneration] = useState(0);
  const [userPaused, setUserPaused] = useState(false);
  const [state, setState] = useState<AskDemoState>({
    ...freshFrame(0),
    receipts: SEED_RECEIPTS,
  });

  const holds = useRef({ user: false, hover: false, focus: false, offscreen: false });
  const panelRef = useRef<HTMLDivElement>(null);
  // Declared before the runner effect so it is current when that effect starts in the same commit.
  const indexRef = useRef(state.index);
  const enteredRef = useRef(false);
  useEffect(() => {
    indexRef.current = state.index;
  }, [state.index]);

  // The pause button stops everything that moves on its own, including the header logo's twinkle.
  useEffect(() => {
    holds.current.user = userPaused;
    setLogoMotionPaused(userPaused);
  }, [userPaused]);
  useEffect(() => () => setLogoMotionPaused(false), []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel || typeof IntersectionObserver === 'undefined') return undefined;
    // `threshold` only controls when the callback fires; the 25% rule is applied to the ratio here.
    const observer = new IntersectionObserver(
      ([entry]) => {
        holds.current.offscreen = !entry.isIntersecting || entry.intersectionRatio < 0.25;
      },
      { threshold: [0, 0.25] }
    );
    observer.observe(panel);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (reducedMotion) {
      // Finish whatever is on screen, including a scenario autoplay reached before the preference changed.
      setState((s) => finishedState(s.index, scenarios, s.receipts));
      return undefined;
    }

    let cancelled = false;
    let elapsed = 0;
    let budget = 1;
    let lastProgressPush = 0;

    const held = () => {
      const h = holds.current;
      return h.user || h.hover || h.focus || h.offscreen || document.hidden;
    };

    // Waits `ms` of *unheld* time; rejects once this run is superseded. `holdable: false` runs on wall-clock
    // time instead, for steps that must finish once started (see the swap in `play`).
    const wait = (ms: number, { holdable = true } = {}) =>
      new Promise<void>((resolve, reject) => {
        let left = ms;
        let last = performance.now();
        const tick = () => {
          if (cancelled) {
            reject(CANCELLED);
            return;
          }
          const now = performance.now();
          if (!holdable || !held()) {
            left -= now - last;
            elapsed += now - last;
            if (now - lastProgressPush > 100) {
              lastProgressPush = now;
              const progress = Math.min(elapsed / budget, 0.985);
              setState((s) => ({ ...s, progress }));
            }
          }
          last = now;
          if (left <= 0) resolve();
          else setTimeout(tick, Math.min(32, left));
        };
        tick();
      });

    const patch = (p: Partial<AskDemoState>) => setState((s) => ({ ...s, ...p }));

    // `advancing`: autoplay moving on from a finished scenario, which fades the old one out first.
    // A run's first scenario is already on screen (initial render or `select`), so it never waits
    // on the clock to appear — a paused demo still shows the scenario the visitor picked.
    const play = async (index: number, advancing: boolean) => {
      const s = scenarios[index];
      const tokens = tokenizeAnswer(s.answer);
      elapsed = 0;
      budget = estimateMs(s);

      if (advancing) {
        // The fade-out is a CSS transition on wall-clock time, so the swap must complete on wall-clock
        // time too: a hover or pause landing mid-swap then holds on the next scenario, never on a blank
        // panel.
        patch({ phase: 'swap', progress: 0 });
        await wait(280, { holdable: false });
        patch(freshFrame(index));
      }
      await wait(420);
      patch({ phase: 'typing' });

      for (let c = 1; c <= s.question.length; c += 1) {
        patch({ typed: c });
        const ch = s.question[c - 1];
        let delay = between(22, 58);
        if (ch === ' ') delay += between(0, 40);
        if (/[,?¿]/.test(ch)) delay += 140;
        await wait(delay);
      }
      await wait(260);

      for (let r = 0; r < s.reading.length; r += 1) {
        patch({ phase: 'reading', readingStep: r });
        await wait(r === s.reading.length - 1 ? 380 : 260);
      }
      patch({ phase: 'found', readingStep: -1 });
      window.dispatchEvent(new Event(LOGO_TWINKLE_EVENT));
      await wait(420);

      patch({ phase: 'answering' });
      for (let t = 1; t <= tokens.length; t += 1) {
        patch({ shownTokens: t });
        const token = tokens[t - 1];
        if (token.kind === 'cite') await wait(170);
        else await wait(between(34, 62) + (/[.,]$/.test(token.text) ? 120 : 0));
      }
      await wait(200);
      patch({ sourcesShown: true });
      await wait(260 + s.sources.length * 140);
      patch({ phase: 'done' });

      await wait(holdMs(s));
      const receipt: AskReceipt = {
        id: `${s.id}-${Date.now()}`,
        channel: s.channelLabel,
        meta: s.askerName,
        question: s.question,
        answer: s.answer.match(/^.*?[.?!](?=\s|$)/)?.[0] ?? s.answer,
      };
      setState((prev) => ({
        ...prev,
        progress: 1,
        receipts: [receipt, ...prev.receipts].slice(0, MAX_RECEIPTS),
      }));
      await wait(250);
    };

    // Every run starts from the scenario on screen, reset to a fresh frame: after `select` that is the
    // chosen one (already fresh); after reduced motion is switched off it is the one it finished.
    const start = indexRef.current;
    patch(freshFrame(start));

    (async () => {
      // Only the very first run waits for the page's entrance animation.
      if (!enteredRef.current) {
        await wait(FIRST_DELAY_MS);
        enteredRef.current = true;
      }
      for (let i = start, advancing = false; ; i = (i + 1) % scenarios.length, advancing = true)
        await play(i, advancing);
    })().catch((error) => {
      if (error !== CANCELLED) throw error;
    });

    return () => {
      cancelled = true;
    };
  }, [generation, reducedMotion, scenarios]);

  // Show the chosen scenario immediately, independent of the playback clock; the new run then plays it.
  const select = useCallback((index: number) => {
    enteredRef.current = true; // an explicit choice doesn't wait out the entrance delay
    setState((s) => ({ ...s, ...freshFrame(index) }));
    setGeneration((g) => g + 1);
  }, []);

  const panelHandlers = {
    onMouseEnter: () => {
      holds.current.hover = true;
    },
    onMouseLeave: () => {
      holds.current.hover = false;
    },
    // Focus inside the panel holds the demo, except on its own play/pause control.
    onFocus: (e: React.FocusEvent) => {
      holds.current.focus = !(e.target as HTMLElement).dataset.demoControl;
    },
    onBlur: () => {
      holds.current.focus = false;
    },
  };

  return {
    state,
    reducedMotion,
    select,
    userPaused,
    togglePause: () => setUserPaused((p) => !p),
    panelRef,
    panelHandlers,
  };
}
